import {
  notifyWorkspacesDeleted,
  openWorkspace,
  closeAllWorkspaces,
  confirmDeleteWorkspace,
  componentInWorkspaceNavigationRequested,
  workspaceNavigationRequested,
  copyWorkspace,
  copyWorkspaceSucceeded,
  workspaceNamespace,
  removeWorkspaces,
  resetWorkspacesWithData,
  requestCloseAllDatasets,
} from './actions';
import { tap, withLatestFrom, exhaustMap, filter } from 'rxjs/operators';
import {
  collectRoutines,
  dispatchAction,
  routine,
  extractPayload,
  ofType,
  spreadFilter,
} from '@ardoq/rxbeach';
import { confirmDeleteAsset } from 'components/DeleteAssetModal/DeleteAssetModal';
import { DeleteType } from 'components/DeleteAssetModal/types';
import { hideRightPane } from 'appContainer/actions';
import { logError, logWarn } from '@ardoq/logging';
import { contextInterface } from 'modelInterface/contextInterface';
import { componentInterface } from '@ardoq/component-interface';
import { workspaceInterface } from '@ardoq/workspace-interface';
import { surveys$ } from '../surveys/surveys$';
import { workspaceApi, componentApi, handleError, api } from '@ardoq/api';
import { renameRoutine } from '../assets/routines';
import { isArdoqError, Result } from '@ardoq/common-helpers';
import { confirmCloseAllDatasets } from 'viewpointNavigator/confirmCloseAllDatasets';
import { requestShowAppModule } from 'appContainer/actions';
import { AppModules } from 'appContainer/types';
import { loadedState$ } from 'loadedState/loadedState$';
import { context$ } from 'streams/context/context$';
import { currentUser$ } from 'streams/currentUser/currentUser$';
import * as contextActions from 'streams/context/ContextActions';
import { isAnyWorkspaceOrLoadedStateOpen } from 'viewpointBuilder/utils';
import { dispatchActionAndWaitForResponse } from 'actions/utils';
import { modelNamespace } from 'streams/models/actions';
import { apiFetchCurrentUserPermissions } from 'streams/currentUserPermissions/actions';
import { openCopiedEntityModal } from 'copyComponent/copyModal';
import { getWorkspaceNavigationOptions } from 'components/AssetsBrowser2024/workspaceNavigationOptions';
import { navigate } from 'router/declarative/streamRouterAdapter/actions';
import { toInventoryLocation } from 'inventory/inventoryLocation$';
import { InventoryModule } from 'inventory/consts';
import { i18nLocale$ } from 'streams/i18nLocale$';

// WebSocket DELETE handling imports
import { websocket$ } from 'sync/websocket$';
import { ArdoqEvent, EventType } from 'sync/types';
import {
  ResourceType,
  APIWorkspaceAttributes,
  CreateWorkspaceResponse,
  ArdoqId,
} from '@ardoq/api-types';
import {
  ardoqEventOperations,
  shouldProcessEventForBranch,
} from 'sync/ardoqEventOperations';
import { activeBranchId$ } from 'streams/activeScenario/activeBranchId$';
import { dataInterface } from '@ardoq/data-interface';
import { handleDelete, handleFetchAll } from 'streams/crud/routines';
import {
  deleteRequested,
  fetchAllFailed,
  fetchAllRequested,
  fetchAllSucceeded,
} from 'streams/crud/actions';
import { workspaceOverviewNamespace } from './workspaceOverviews$';

const handleOpenWorkspace = routine(
  ofType(openWorkspace),
  extractPayload(),
  tap(({ workspaceId, keepCurrentContext, trackingLocation }) => {
    if (workspaceInterface.workspaceExists(workspaceId)) {
      const switchContextToNewWorkspace = !(
        keepCurrentContext && contextInterface.getActiveWorkspaceId()
      );
      if (switchContextToNewWorkspace) {
        contextInterface.switchToWorkspace(workspaceId, trackingLocation);
      } else {
        contextInterface.loadWorkspacesInBackground(
          [workspaceId],
          trackingLocation
        );
      }
    } else {
      logError(
        new Error(`Missing workspace for id ${workspaceId}`),
        `Cannot open a workspace with id ${workspaceId}, because it is not in the collection.`
      );
    }
  })
);

const handleWorkspaceNavigationRequested = routine(
  ofType(workspaceNavigationRequested),
  extractPayload(),
  withLatestFrom(context$),
  tap(([{ workspaceId, trackingLocation }, context]) => {
    if (context.workspaceId === workspaceId) {
      dispatchAction(
        requestShowAppModule({ selectedModule: AppModules.WORKSPACES })
      );
      return;
    }
    contextInterface.switchToWorkspace(workspaceId, trackingLocation);
  })
);

const ensureWorkspaceOfComponentIsLoaded = async (
  componentId: ArdoqId
): Promise<boolean> => {
  const rootWorkspace = componentInterface.getWorkspaceId(componentId);
  const isLoadedWorkspace = Boolean(
    rootWorkspace &&
    contextInterface.getLoadedWorkspaceIds().includes(rootWorkspace)
  );
  if (isLoadedWorkspace) {
    return isLoadedWorkspace;
  }
  const component = await componentApi.fetch(componentId);
  if (isArdoqError(component)) {
    logError(
      Error('Failed to load component in ensureWorkspaceOfComponentIsLoaded')
    );
    return false;
  }
  await contextInterface.switchToWorkspace(component.rootWorkspace);
  return true;
};

const openComponentInWorkspace = async (componentId: ArdoqId) => {
  const isWorkspaceLoaded =
    await ensureWorkspaceOfComponentIsLoaded(componentId);
  if (!isWorkspaceLoaded) {
    return;
  }
  if (componentInterface.isComponent(componentId)) {
    dispatchAction(contextActions.setComponentId(componentId));
  } else {
    dispatchAction(contextActions.clearComponent());
  }
  dispatchAction(
    requestShowAppModule({ selectedModule: AppModules.WORKSPACES })
  );
};

// TODO: remove when COMPONENT_LANDING_PAGE feature flag is removed
const handleOpenComponentInWorkspace = routine(
  ofType(componentInWorkspaceNavigationRequested),
  extractPayload(),
  withLatestFrom(loadedState$, context$, currentUser$),
  tap(async ([{ componentId }, { loadedStates }, context, currentUser]) => {
    const isSomeViewpointOpen = isAnyWorkspaceOrLoadedStateOpen(
      loadedStates,
      context
    );

    if (isSomeViewpointOpen) {
      if (!currentUser) return;
      const confirm = await confirmCloseAllDatasets(currentUser);
      if (!confirm) return;
      dispatchAction(closeAllWorkspaces());
    }

    openComponentInWorkspace(componentId);
  })
);

const handleConfirmDeleteWorkspace = routine(
  ofType(confirmDeleteWorkspace),
  extractPayload(),
  withLatestFrom(surveys$),
  tap(async ([{ name, _id }, { list: surveys }]) => {
    const affectedSurveys = surveys
      ? surveys.filter(survey => survey.workspace === _id)
      : [];

    const approved = await confirmDeleteAsset({
      deleteType: DeleteType.WORKSPACE,
      name,
      id: _id,
      affectedSurveys: affectedSurveys,
    });

    if (approved) {
      dispatchAction(hideRightPane());
      dispatchAction(deleteRequested(workspaceNamespace, _id));
    }
  })
);

const handleCloseAllWorkspaces = routine(
  ofType(closeAllWorkspaces),
  tap(() => {
    dispatchAction(contextActions.closeAllWorkspaces());
  })
);

const handleRequestCloseAllDatasets = routine(
  ofType(requestCloseAllDatasets),
  withLatestFrom(currentUser$),
  tap(async ([, currentUser]) => {
    if (!currentUser) return;
    const confirmed = await confirmCloseAllDatasets(currentUser);
    if (!confirmed) return;
    dispatchAction(closeAllWorkspaces());
  })
);

const handleCopyWorkspace = routine(
  ofType(copyWorkspace),
  extractPayload(),
  exhaustMap(
    ({
      sourceWorkspaceId,
      newName,
      copyComponents,
      copyReferences,
      copyCrossWorkspaceReferences,
    }) =>
      workspaceApi.copy(
        sourceWorkspaceId,
        newName,
        copyComponents,
        copyReferences,
        copyCrossWorkspaceReferences
      )
  ),
  handleError(logError),
  tap(async response => {
    const workspacesOrError = await workspaceApi.fetchAll();
    if (isArdoqError(workspacesOrError)) {
      api.logErrorIfNeeded(workspacesOrError);
      return;
    }
    dispatchAction(resetWorkspacesWithData({ workspaces: workspacesOrError }));
    dispatchAction(copyWorkspaceSucceeded());

    dispatchActionAndWaitForResponse(
      fetchAllRequested(modelNamespace),
      fetchAllSucceeded,
      fetchAllFailed,
      modelNamespace
    );
    dispatchAction(apiFetchCurrentUserPermissions());

    const newWorkspace = response.data.workspaces[0];
    const openInInventory = () =>
      dispatchAction(
        navigate(
          toInventoryLocation({
            inventoryModule: InventoryModule.BROWSE,
            selectedWorkspaceIds: [newWorkspace.id],
            dataSourceType: 'workspace',
            useCase: 'pageManageData',
            filterSubquery: null,
          })
        )
      );
    const openInWorkspace = () =>
      dispatchAction(
        workspaceNavigationRequested({
          workspaceId: newWorkspace.id,
          trackingLocation: 'copyWorkspaceDialog',
        })
      );
    const navigationOptions = getWorkspaceNavigationOptions({
      i18nLocale: i18nLocale$.state,
      onOpenInInventory: openInInventory,
      onOpenInWorkspace: openInWorkspace,
    });
    openCopiedEntityModal({
      entityType: 'workspace',
      copyName: newWorkspace.name,
      copyId: newWorkspace.id,
      openCopy: openInInventory,
      navigationOptions,
    });
  })
);

const isWorkspaceCreateEvent = (
  event: ArdoqEvent<unknown>
): event is ArdoqEvent<CreateWorkspaceResponse> =>
  Boolean(ardoqEventOperations.isTransactionEvent(event)) &&
  ardoqEventOperations.isOfResourceType(event, ResourceType.WORKSPACE);

const isWorkspaceEventToBeHandled = (
  event: ArdoqEvent<unknown>
): event is
  | ArdoqEvent<APIWorkspaceAttributes>
  | ArdoqEvent<CreateWorkspaceResponse> => {
  if (!ardoqEventOperations.isOfResourceType(event, ResourceType.WORKSPACE)) {
    return false;
  }

  const eventType = event['event-type'];
  return (
    eventType === EventType.DELETE ||
    eventType === EventType.UPDATE ||
    isWorkspaceCreateEvent(event)
  );
};

const handleWorkspaceEvent = (
  event:
    | ArdoqEvent<APIWorkspaceAttributes>
    | ArdoqEvent<CreateWorkspaceResponse>
): void => {
  if (isWorkspaceCreateEvent(event)) {
    dataInterface.handleWorkspaceCreatedTransaction(event.data);
    return;
  }
  switch (event['event-type']) {
    case EventType.DELETE: {
      const workspaceId = event.data._id;
      dispatchAction(removeWorkspaces([workspaceId]));
      if (contextInterface.getLoadedWorkspaceIds().includes(workspaceId)) {
        dataInterface.handleWorkspaceDeleted(workspaceId);
        dispatchAction(
          contextActions.closeWorkspace({
            workspaceId,
            options: { isRegisterLoadedState: true },
          })
        );
      }

      dispatchAction(notifyWorkspacesDeleted({ workspaceIds: [workspaceId] }));
      break;
    }
    case EventType.UPDATE:
      // UPDATE events are handled by websocketReducers in workspace[Overview]s$.ts
      break;
    default:
      logWarn(new Error('unknown workspace event-type'), event['event-type']);
  }
};

// We need this adapter since workpaceApi can delete many workspaces
// in one go
const doDelete = (_id: ArdoqId): Promise<Result<ArdoqId>> =>
  workspaceApi.deleteWorkspaces([_id]).then(result => {
    if (isArdoqError(result)) {
      return result;
    }
    return result[0];
  });

export const routines = collectRoutines(
  handleConfirmDeleteWorkspace,
  handleOpenWorkspace,
  handleWorkspaceNavigationRequested,
  handleOpenComponentInWorkspace,
  handleCloseAllWorkspaces,
  handleRequestCloseAllDatasets,
  handleCopyWorkspace,
  handleFetchAll(workspaceNamespace, workspaceApi.fetchAll),
  handleFetchAll(workspaceOverviewNamespace, workspaceApi.overview),
  handleDelete(workspaceNamespace, doDelete),
  renameRoutine(workspaceNamespace, workspaceApi.rename),
  websocket$.pipe(
    filter(isWorkspaceEventToBeHandled),
    withLatestFrom(activeBranchId$),
    spreadFilter(shouldProcessEventForBranch),
    tap(handleWorkspaceEvent)
  )
);
